import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { dispositivosService, api } from '../services/api';
import { FiPlus, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import MapaUbicacion from '../components/MapaUbicacion';
import KioskToggle from '../components/KioskToggle';
import DevModeToggle from '../components/DevModeToggle';
import UnknownSourcesToggle from '../components/UnknownSourcesToggle';
import UbicacionToggle from '../components/UbicacionToggle';
import DispositivoDetallesModal from '../components/DispositivoDetallesModal';
import toast from 'react-hot-toast';

// Estilos para los badges de estado
const statusBadgeStyles = `
  .status-badge {
    @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium;
  }
  .status-online {|
    @apply bg-green-100 text-green-800;
  }
  .status-warning {
    @apply bg-yellow-100 text-yellow-800;
  }
  .status-offline {
    @apply bg-red-100 text-red-800;
  }
`;

const Dispositivos = () => {
  const queryClient = useQueryClient();
  
  const [filtros, setFiltros] = useState({
    marca: '',
    android_version: '',
    buscar: ''
  });
  
  const [paginacion, setPaginacion] = useState({
    pagina: 1,
    limite: 25 // Aumentado de 10 a 25 para reducir número de peticiones
  });

  const [dispositivoSeleccionado, setDispositivoSeleccionado] = useState(null);
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [ubicacionMapa, setUbicacionMapa] = useState(null);
  const [mostrarModalSim, setMostrarModalSim] = useState(false);
  const [simInfo, setSimInfo] = useState(null);
  const [estadosKiosk, setEstadosKiosk] = useState({});
  const [estadosDev, setEstadosDev] = useState({});
  const [estadosOri, setEstadosOri] = useState({});
  const [estadosUbicacion, setEstadosUbicacion] = useState({});
  const [modalDetalles, setModalDetalles] = useState({ isOpen: false, dispositivo: null });
  const [codigoSeleccionado, setCodigoSeleccionado] = useState({ dispositivoId: null, codigo: null });
  const [mostrarModalCodigo, setMostrarModalCodigo] = useState(false);
  const [mostrarModalCodigosUsados, setMostrarModalCodigosUsados] = useState(false);
  const [codigosUsadosData, setCodigosUsadosData] = useState({ dispositivo: null, codigos: [] });

    const { data, isLoading, error } = useQuery(
    ['dispositivos', filtros, paginacion],
    async () => {
      const params = {
        ...paginacion,
        ...filtros
      };
      
      // Solo incluir filtros que tengan valor
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await dispositivosService.obtenerTodos(params);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  const dispositivos = useMemo(() => data?.devices || [], [data?.devices]);
  const paginacionData = useMemo(() => ({ 
    total: data?.total_dispositivos || 0,
    paginas: Math.ceil((data?.total_dispositivos || 0) / paginacion.limite),
    pagina: paginacion.pagina,
    limite: paginacion.limite
  }), [data?.total_dispositivos, paginacion.pagina, paginacion.limite]);

  // Mutación para desaprovisionar dispositivo
  const desaprovisionarDispositivo = useMutation({
    mutationFn: (id) => {
      return dispositivosService.desaprovisionar(id);
    },
    onSuccess: (data, variables) => {
      toast.success('Dispositivo desaprovisionado correctamente');
      queryClient.invalidateQueries(['dispositivos']);
    },
    onError: (error, variables) => {
      console.error('❌ Error en desaprovisionamiento para ID:', variables, 'Error:', error);
      toast.error('Error al desaprovisionar dispositivo: ' + (error.response?.data?.message || error.message));
    }
  });
  
  // Inicializar estados locales cuando se cargan los dispositivos
  useEffect(() => {
    if (dispositivos.length > 0 && data) {
      const estadosKioskIniciales = {};
      const estadosDevIniciales = {};
      const estadosOriIniciales = {};
      const estadosUbicacionIniciales = {};
      
      dispositivos.forEach(dispositivo => {
        // Usar el estado de la BD, no el estado local
        estadosKioskIniciales[dispositivo.id] = dispositivo.estado_kiosk || 'unlocked';
        estadosDevIniciales[dispositivo.id] = dispositivo.estado_dev || 'disabled';
        estadosOriIniciales[dispositivo.id] = dispositivo.estado_ori || 'disabled';
        estadosUbicacionIniciales[dispositivo.id] = dispositivo.estado_location_restriction || 'disabled';
      });
      
      setEstadosKiosk(estadosKioskIniciales);
      setEstadosDev(estadosDevIniciales);
      setEstadosOri(estadosOriIniciales);
      setEstadosUbicacion(estadosUbicacionIniciales);
    }
  }, [dispositivos, data]);
  

  



  // Mutación para crear comandos
  const crearComando = useMutation(
    async ({ dispositivo_id, tipo }) => {
      // Obtener el código del dispositivo usando el ID
      const dispositivo = dispositivos.find(d => d.id === dispositivo_id);
      if (!dispositivo) {
        throw new Error('Dispositivo no encontrado');
      }
      
      // Enviar comando usando el endpoint correcto /api/comando/:deviceCode
      const response = await api.post(`/comando/${dispositivo.codigo}`, {
        command: tipo
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Comando enviado exitosamente');
        // Invalidar la cache para refrescar los datos de la base de datos
        queryClient.invalidateQueries(['dispositivos']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al enviar comando');
        // En caso de error, revertir el estado local
        const dispositivo = dispositivos.find(d => d.id === error.dispositivo_id);
        if (dispositivo) {
          const estadoAnterior = dispositivo.estado_kiosk || 'unlocked';
          setEstadosKiosk(prev => ({
            ...prev,
            [dispositivo.id]: estadoAnterior
          }));
        }
      }
    }
  );

  const manejarCambioFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
    setPaginacion(prev => ({ ...prev, pagina: 1 })); // Resetear a primera página
  };

  const handleCambiarPagina = (nuevaPagina) => {
    setPaginacion(prev => ({ ...prev, pagina: nuevaPagina }));
  };

  // Función para desaprovisionar dispositivo
  const handleDesaprovisionar = (dispositivoId) => {
    if (window.confirm('¿Estás seguro de que quieres desaprovisionar este dispositivo? Esta acción no se puede deshacer.')) {
      // Usar el mismo sistema que otros comandos para que se envíe por WebSocket
      crearComando.mutate({ dispositivo_id: dispositivoId, tipo: 'desaprovisionar' });
    }
  };

  // Función para verificar si un dispositivo está desaprovisionado
  const estaDesaprovisionado = (dispositivo) => {
    return dispositivo.estado_desaprovisionado === 'enabled';
  };

  const handleCambiarLimite = (nuevoLimite) => {
    setPaginacion({ pagina: 1, limite: nuevoLimite });
  };

  const obtenerEstadoClases = (ultimaActualizacion) => {
    if (!ultimaActualizacion) return 'status-badge status-offline';
    
    const ultima = new Date(ultimaActualizacion);
    const ahora = new Date();
    const diferencia = ahora - ultima;
    const minutos = Math.floor(diferencia / (1000 * 60));
    
    if (minutos < 5) {
      return 'status-badge status-online';
    } else if (minutos < 30) {
      return 'status-badge status-warning';
    } else {
      return 'status-badge status-offline';
    }
  };

  const obtenerEstadoTexto = (ultimaActualizacion) => {
    if (!ultimaActualizacion) return 'Desconectado';
    
    const ultima = new Date(ultimaActualizacion);
    const ahora = new Date();
    const diferencia = ahora - ultima;
    const minutos = Math.floor(diferencia / (1000 * 60));
    
    if (minutos < 5) {
      return 'Conectado';
    } else if (minutos < 30) {
      return 'Inactivo';
    } else {
      return 'Desconectado';
    }
  };

  const enviarComando = (dispositivo_id, tipo) => {
    crearComando.mutate({ dispositivo_id, tipo });
  };

  const toggleKiosk = (dispositivo_id, comando) => {
    // Actualizar estado local inmediatamente
    const nuevoEstado = comando === 'start_kiosk' ? 'locked' : 'unlocked';
    setEstadosKiosk(prev => ({
      ...prev,
      [dispositivo_id]: nuevoEstado
    }));
    
    // Enviar comando después de actualizar el estado local
    enviarComando(dispositivo_id, comando);
  };

  const toggleDevMode = (dispositivo_id, comando) => {
    // Actualizar estado local inmediatamente
    const nuevoEstado = comando === 'enable_developer_mode' ? 'enabled' : 'disabled';
    setEstadosDev(prev => ({
      ...prev,
      [dispositivo_id]: nuevoEstado
    }));
    
    // Enviar comando después de actualizar el estado local
    enviarComando(dispositivo_id, comando);
  };

  const toggleUnknownSources = (dispositivo_id, comando) => {
    // Actualizar estado local inmediatamente
    const nuevoEstado = comando === 'enable_unknown_sources' ? 'enabled' : 'disabled';
    setEstadosOri(prev => ({
      ...prev,
      [dispositivo_id]: nuevoEstado
    }));
    
    // Enviar comando después de actualizar el estado local
    enviarComando(dispositivo_id, comando);
  };

  const toggleUbicacion = (dispositivo_id, comando) => {
    const nuevoEstado = comando === 'enable_location_restriction' ? 'enabled' : 'disabled';
    setEstadosUbicacion(prev => ({
      ...prev,
      [dispositivo_id]: nuevoEstado
    }));
    enviarComando(dispositivo_id, comando);
  };

  const abrirModalDetalles = (dispositivo) => {
    setModalDetalles({ isOpen: true, dispositivo });
  };

  const cerrarModalDetalles = () => {
    setModalDetalles({ isOpen: false, dispositivo: null });
  };

  const verDetalles = (dispositivo) => {
    setDispositivoSeleccionado(dispositivo);
    setMostrarDetalles(true);
  };

  const cerrarDetalles = () => {
    setMostrarDetalles(false);
    setDispositivoSeleccionado(null);
  };

  const abrirMapa = (ubicacion) => {
    setUbicacionMapa(ubicacion);
    setMostrarMapa(true);
  };

  const cerrarMapa = () => {
    setMostrarMapa(false);
    setUbicacionMapa(null);
  };

  const abrirModalSim = (dispositivo) => {
    if (dispositivo.sim_info && dispositivo.sim_info !== 'null' && dispositivo.sim_info !== null) {
      setSimInfo({
        ...dispositivo.sim_info,
        dispositivoCodigo: dispositivo.codigo
      });
      setMostrarModalSim(true);
    } else {
      toast.error('No hay información de SIM disponible. Envía el comando para obtenerla.');
    }
  };

  const cerrarModalSim = () => {
    setMostrarModalSim(false);
    setSimInfo(null);
  };

  const obtenerInfoSim = async (dispositivo) => {
    try {
      // Enviar comando para obtener información de SIM
      await enviarComando(dispositivo.id, 'get_sim_info');
    } catch (error) {
      console.error('Error obteniendo información de SIM:', error);
      toast.error('Error al enviar comando para obtener información de SIM');
    }
  };

  // Función para obtener un código de desbloqueo aleatorio
  const obtenerCodigoAleatorio = async (dispositivo) => {
    try {
      const response = await dispositivosService.obtenerCodigoDesbloqueo(dispositivo.codigo);
      if (response.data.ok) {
        setCodigoSeleccionado({
          dispositivoId: dispositivo.id,
          codigo: response.data.codigo,
          dispositivoCodigo: dispositivo.codigo,
          codigosDisponibles: response.data.codigosDisponibles,
          codigosUsados: response.data.codigosUsados,
          totalCodigos: response.data.totalCodigos
        });
        setMostrarModalCodigo(true);
        // Invalidar la cache para refrescar los datos
        queryClient.invalidateQueries(['dispositivos']);
        toast.success('Código de desbloqueo seleccionado');
      } else {
        toast.error(response.data.error || 'Error al obtener código de desbloqueo');
      }
    } catch (error) {
      console.error('Error obteniendo código de desbloqueo:', error);
      toast.error(error.response?.data?.error || 'Error al obtener código de desbloqueo');
    }
  };

  // Función para generar 30 nuevos códigos de desbloqueo (usa el endpoint /api/generar-codigos-desbloqueo/:deviceCode)
  const generarNuevosCodigosDesbloqueo = async (dispositivo) => {
    try {
      const response = await dispositivosService.generarCodigosDesbloqueo(dispositivo.codigo);
      if (response.data.ok) {
        toast.success(response.data.message || 'Códigos de desbloqueo generados correctamente');
        // Refrescar la lista de dispositivos para actualizar los contadores de códigos
        queryClient.invalidateQueries(['dispositivos']);
      } else {
        toast.error(response.data.error || 'Error al generar códigos de desbloqueo');
      }
    } catch (error) {
      console.error('Error generando códigos de desbloqueo:', error);
      toast.error(error.response?.data?.error || 'Error al generar códigos de desbloqueo');
    }
  };

  const cerrarModalCodigo = () => {
    setMostrarModalCodigo(false);
    setCodigoSeleccionado({ dispositivoId: null, codigo: null });
  };

  const abrirModalCodigosUsados = (dispositivo) => {
    if (dispositivo.codigos_desbloqueo_usados && 
        Array.isArray(dispositivo.codigos_desbloqueo_usados) && 
        dispositivo.codigos_desbloqueo_usados.length > 0) {
      // Ordenar de manera descendente (último ingresado primero)
      const codigosOrdenados = [...dispositivo.codigos_desbloqueo_usados].reverse();
      setCodigosUsadosData({
        dispositivo: dispositivo,
        codigos: codigosOrdenados
      });
      setMostrarModalCodigosUsados(true);
    } else {
      toast.error('No hay códigos usados para este dispositivo');
    }
  };

  const cerrarModalCodigosUsados = () => {
    setMostrarModalCodigosUsados(false);
    setCodigosUsadosData({ dispositivo: null, codigos: [] });
  };



  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error al cargar los dispositivos</p>
        <pre className="mt-4 text-xs bg-gray-100 p-4 rounded overflow-auto">
          {JSON.stringify(error, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <>
      <style>{statusBadgeStyles}</style>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dispositivos</h1>
          <p className="text-gray-600">Gestiona todos los dispositivos móviles</p>
        </div>
        <button className="btn-primary mt-4 sm:mt-0 flex items-center">
          <FiPlus size={16} className="mr-2" />
          Agregar Dispositivo
        </button>
      </div>



      {/* Filtros */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar dispositivos..."
                className="input-field pl-10"
                value={filtros.buscar}
                onChange={(e) => manejarCambioFiltro('buscar', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marca
            </label>
            <select
              className="input-field"
              value={filtros.marca}
              onChange={(e) => manejarCambioFiltro('marca', e.target.value)}
            >
              <option value="">Todas</option>
              <option value="Xiaomi">Xiaomi</option>
              <option value="TECNO">TECNO</option>
              <option value="ZTE">ZTE</option>
              <option value="Samsung">Samsung</option>
              <option value="Huawei">Huawei</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Android
            </label>
            <select
              className="input-field"
              value={filtros.android_version}
              onChange={(e) => manejarCambioFiltro('android_version', e.target.value)}
            >
              <option value="">Todas</option>
              <option value="13">Android 13</option>
              <option value="14">Android 14</option>
              <option value="15">Android 15</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mostrar
            </label>
                         <select
               className="input-field"
               value={paginacion.limite}
               onChange={(e) => handleCambiarLimite(parseInt(e.target.value))}
             >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de dispositivos */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dispositivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Actualización
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Batería
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Versión App
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ubicación
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SIM
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kiosk
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Modo Desarrollador
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Orígenes Desconocidos
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ubicacion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código Desbloqueo
                </th>

                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dispositivos.map((dispositivo) => (
                <tr key={dispositivo.id} className="hover:bg-gray-50">
                                                       <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => abrirModalDetalles(dispositivo)}
                      className="text-left hover:bg-blue-50 px-2 py-1 rounded transition-colors cursor-pointer w-full"
                      title="Haz clic para ver detalles del dispositivo"
                    >
                      <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                        {dispositivo.codigo}
                      </div>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={obtenerEstadoClases(dispositivo.ultima_actualizacion)}>
                      {obtenerEstadoTexto(dispositivo.ultima_actualizacion)}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {dispositivo.ultima_actualizacion 
                      ? new Date(dispositivo.ultima_actualizacion).toLocaleString('es-ES')
                      : 'Nunca'
                    }
                  </td>
                  {/* Columna Estado */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {estaDesaprovisionado(dispositivo) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Desaprovisionado
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Activo
                      </span>
                    )}
                  </td>
                  
                  {/* Columna Batería */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {dispositivo.bateria !== null && dispositivo.bateria !== undefined ? (
                      <div className="flex items-center justify-center">
                        <span className={`text-sm font-semibold ${
                          dispositivo.bateria >= 50 
                            ? 'text-green-600' 
                            : dispositivo.bateria >= 20 
                            ? 'text-yellow-600' 
                            : 'text-red-600'
                        }`}>
                          {dispositivo.bateria}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  
                  {/* Columna Versión App */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {dispositivo.app_version ? (
                      <span className="text-xs font-mono text-gray-700">{dispositivo.app_version}</span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  
                  {/* Columna Ubicación */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col gap-2 items-center">
                      {/* Botón para abrir mapa (si hay ubicación) */}
                      {dispositivo.ubicacion && 
                       dispositivo.ubicacion !== 'null' && 
                       dispositivo.ubicacion !== null &&
                       typeof dispositivo.ubicacion === 'object' &&
                       dispositivo.ubicacion.lat && 
                       dispositivo.ubicacion.lng ? (
                        <button
                          onClick={() => abrirMapa(dispositivo.ubicacion)}
                          disabled={estaDesaprovisionado(dispositivo)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                            estaDesaprovisionado(dispositivo)
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          title={estaDesaprovisionado(dispositivo) ? 'Dispositivo desaprovisionado' : 'Ver en el mapa'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
                          </svg>
                        </button>
                      ) : (
                        <div className="w-8 h-8 flex items-center justify-center">
                          <span className="text-xs text-gray-400">-</span>
                        </div>
                      )}
                      
                      {/* Botón para obtener ubicación */}
                      <button
                        onClick={() => enviarComando(dispositivo.id, 'get_location')}
                        disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                          estaDesaprovisionado(dispositivo)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={estaDesaprovisionado(dispositivo) ? 'Dispositivo desaprovisionado' : 'Obtener ubicación actual'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  
                  {/* Columna SIM */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex flex-col gap-2 items-center">
                      {/* Botón para ver información de SIM (si hay información) */}
                      {dispositivo.sim_info && 
                       dispositivo.sim_info !== 'null' && 
                       dispositivo.sim_info !== null &&
                       typeof dispositivo.sim_info === 'object' ? (
                        <button
                          onClick={() => abrirModalSim(dispositivo)}
                          disabled={estaDesaprovisionado(dispositivo)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                            estaDesaprovisionado(dispositivo)
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          title={estaDesaprovisionado(dispositivo) ? 'Dispositivo desaprovisionado' : 'Ver información de SIM'}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                          </svg>
                        </button>
                      ) : null}

                      {/* Botón para obtener información de SIM */}
                      <button
                        onClick={() => obtenerInfoSim(dispositivo)}
                        disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                          estaDesaprovisionado(dispositivo)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title={estaDesaprovisionado(dispositivo) ? 'Dispositivo desaprovisionado' : 'Obtener información de SIM'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                        </svg>
                      </button>
                    </div>
                  </td>
                                    {/* Columna Kiosk */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <KioskToggle
                      isKioskMode={
                        estadosKiosk[dispositivo.id] !== undefined 
                          ? estadosKiosk[dispositivo.id] === 'locked'
                          : (dispositivo.estado_kiosk === 'locked')
                      }
                      onToggle={toggleKiosk}
                      disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                      deviceId={dispositivo.id}
                    />
                  </td>
                  
                  {/* Columna Modo Desarrollador */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <DevModeToggle
                      isDevMode={
                        estadosDev[dispositivo.id] !== undefined 
                          ? estadosDev[dispositivo.id] === 'enabled'
                          : (dispositivo.estado_dev === 'enabled')
                      }
                      onToggle={toggleDevMode}
                      disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                      deviceId={dispositivo.id}
                    />
                  </td>
                  
                  {/* Columna Orígenes Desconocidos */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <UnknownSourcesToggle
                      isUnknownSourcesEnabled={
                        estadosOri[dispositivo.id] !== undefined 
                          ? estadosOri[dispositivo.id] === 'enabled'
                          : (dispositivo.estado_ori === 'enabled')
                      }
                      onToggle={toggleUnknownSources}
                      disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                      deviceId={dispositivo.id}
                    />
                  </td>
                  
                  {/* Columna Ubicacion: ON = restricción desactivada (disabled), OFF = restricción activada (enabled) */}
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <UbicacionToggle
                      isUbicacionEnabled={
                        (estadosUbicacion[dispositivo.id] !== undefined
                          ? estadosUbicacion[dispositivo.id]
                          : (dispositivo.estado_location_restriction || 'disabled')) === 'disabled'
                      }
                      onToggle={toggleUbicacion}
                      disabled={crearComando.isLoading || estaDesaprovisionado(dispositivo)}
                      deviceId={dispositivo.id}
                    />
                  </td>
                  
                  {/* Columna Código Desbloqueo */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col gap-2">
                      {/* Información de códigos múltiples */}
                      {dispositivo.codigos_desbloqueo && 
                       Array.isArray(dispositivo.codigos_desbloqueo) && 
                       dispositivo.codigos_desbloqueo.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {/* Estadísticas de códigos */}
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-600">
                              {dispositivo.codigos_desbloqueo.length} total
                            </span>
                            {dispositivo.codigos_desbloqueo_usados && 
                             Array.isArray(dispositivo.codigos_desbloqueo_usados) && 
                             dispositivo.codigos_desbloqueo_usados.length > 0 && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-red-600 font-medium">
                                  {dispositivo.codigos_desbloqueo_usados.length} usados
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-green-600 font-medium">
                                  {dispositivo.codigos_desbloqueo.length - dispositivo.codigos_desbloqueo_usados.length} disponibles
                                </span>
                              </>
                            )}
                            {(!dispositivo.codigos_desbloqueo_usados || 
                              !Array.isArray(dispositivo.codigos_desbloqueo_usados) || 
                              dispositivo.codigos_desbloqueo_usados.length === 0) && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-green-600 font-medium">
                                  {dispositivo.codigos_desbloqueo.length} disponibles
                                </span>
                              </>
                            )}
                          </div>
                          
                          {/* Botones de acción */}
                          <div className="flex gap-2">
                            {/* Botón para obtener código */}
                            <button
                              onClick={() => obtenerCodigoAleatorio(dispositivo)}
                              disabled={estaDesaprovisionado(dispositivo) || 
                                       (dispositivo.codigos_desbloqueo_usados && 
                                        Array.isArray(dispositivo.codigos_desbloqueo_usados) &&
                                        dispositivo.codigos_desbloqueo_usados.length >= dispositivo.codigos_desbloqueo.length)}
                              className={`flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                estaDesaprovisionado(dispositivo) ||
                                (dispositivo.codigos_desbloqueo_usados && 
                                 Array.isArray(dispositivo.codigos_desbloqueo_usados) &&
                                 dispositivo.codigos_desbloqueo_usados.length >= dispositivo.codigos_desbloqueo.length)
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                              title={
                                estaDesaprovisionado(dispositivo) 
                                  ? 'Dispositivo desaprovisionado' 
                                  : (dispositivo.codigos_desbloqueo_usados && 
                                      Array.isArray(dispositivo.codigos_desbloqueo_usados) &&
                                      dispositivo.codigos_desbloqueo_usados.length >= dispositivo.codigos_desbloqueo.length)
                                    ? 'Todos los códigos han sido utilizados'
                                    : 'Obtener código aleatorio no utilizado'
                              }
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 mr-1">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                              </svg>
                              Obtener
                            </button>
                            
                            {/* Botón para ver códigos usados */}
                            {dispositivo.codigos_desbloqueo_usados && 
                             Array.isArray(dispositivo.codigos_desbloqueo_usados) && 
                             dispositivo.codigos_desbloqueo_usados.length > 0 && (
                              <button
                                onClick={() => abrirModalCodigosUsados(dispositivo)}
                                disabled={estaDesaprovisionado(dispositivo)}
                                className={`inline-flex items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                  estaDesaprovisionado(dispositivo)
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                                }`}
                                title={estaDesaprovisionado(dispositivo) ? 'Dispositivo desaprovisionado' : 'Ver códigos utilizados'}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 mr-1">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                </svg>
                                Ver
                              </button>
                            )}
                          </div>
                        </div>
                      ) : dispositivo.codigo_desbloqueo ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {dispositivo.codigo_desbloqueo}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          No configurado
                        </span>
                      )}

                      {/* Botón para generar nuevos códigos de desbloqueo (solo para versión 10.0.0) */}
                      {dispositivo.app_version === '10.0.0' && (
                        <button
                          onClick={() => generarNuevosCodigosDesbloqueo(dispositivo)}
                          disabled={estaDesaprovisionado(dispositivo)}
                          className={`mt-1 inline-flex items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            estaDesaprovisionado(dispositivo)
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                          }`}
                          title={estaDesaprovisionado(dispositivo)
                            ? 'Dispositivo desaprovisionado'
                            : 'Generar 30 nuevos códigos de desbloqueo'}
                        >
                          Generar 30 códigos
                        </button>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        className={`p-2 rounded-md transition-colors ${
                          desaprovisionarDispositivo.isLoading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                        }`}
                        onClick={() => handleDesaprovisionar(dispositivo.id)}
                        disabled={desaprovisionarDispositivo.isLoading}
                        title="Desaprovisionar dispositivo"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {paginacionData && paginacionData.paginas > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handleCambiarPagina(1)}
                disabled={paginacion.pagina === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Primera
              </button>
              <button
                onClick={() => handleCambiarPagina(paginacionData.paginas)}
                disabled={paginacion.pagina === paginacionData.paginas}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Última
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">{((paginacion.pagina - 1) * paginacion.limite) + 1}</span>
                  {' '}a{' '}
                  <span className="font-medium">
                    {Math.min(paginacion.pagina * paginacion.limite, paginacionData.total)}
                  </span>
                  {' '}de{' '}
                  <span className="font-medium">{paginacionData.total}</span>
                  {' '}resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handleCambiarPagina(1)}
                    disabled={paginacion.pagina === 1}
                    className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Primera página</span>
                    1
                  </button>
                  
                  {/* Números de página */}
                  {Array.from({ length: Math.min(5, paginacionData.paginas) }, (_, i) => {
                    let pagina;
                    if (paginacionData.paginas <= 5) {
                      pagina = i + 1;
                    } else if (paginacion.pagina <= 3) {
                      pagina = i + 1;
                    } else if (paginacion.pagina >= paginacionData.paginas - 2) {
                      pagina = paginacionData.paginas - 4 + i;
                    } else {
                      pagina = paginacion.pagina - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pagina}
                        onClick={() => handleCambiarPagina(pagina)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagina === paginacion.pagina
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pagina}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handleCambiarPagina(paginacionData.paginas)}
                    disabled={paginacion.pagina === paginacionData.paginas}
                    className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Última página</span>
                    {paginacionData.paginas}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalles del Dispositivo */}
      {mostrarDetalles && dispositivoSeleccionado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Detalles del Dispositivo: {dispositivoSeleccionado.codigo}
                </h3>
                <button
                  onClick={cerrarDetalles}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Información Básica</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Código:</span> {dispositivoSeleccionado.codigo}</div>
                    <div><span className="font-medium">Marca:</span> {dispositivoSeleccionado.marca}</div>
                    <div><span className="font-medium">Modelo:</span> {dispositivoSeleccionado.modelo}</div>
                    <div><span className="font-medium">Android:</span> {dispositivoSeleccionado.android_version}</div>
                    <div><span className="font-medium">Serial:</span> {dispositivoSeleccionado.serial}</div>
                    <div><span className="font-medium">IMEI:</span> {dispositivoSeleccionado.imei}</div>
                    <div><span className="font-medium">Android ID:</span> {dispositivoSeleccionado.android_id}</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Estado y Conexión</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Estado:</span>
                      <span className={`ml-2 ${obtenerEstadoClases(dispositivoSeleccionado.ultima_actualizacion)}`}>
                        {obtenerEstadoTexto(dispositivoSeleccionado.ultima_actualizacion)}
                      </span>
                    </div>
                    <div><span className="font-medium">Fecha Registro:</span> {new Date(dispositivoSeleccionado.fecha_registro).toLocaleString('es-ES')}</div>
                    <div><span className="font-medium">Última Actualización:</span> {dispositivoSeleccionado.ultima_actualizacion ? new Date(dispositivoSeleccionado.ultima_actualizacion).toLocaleString('es-ES') : 'Nunca'}</div>
                    <div><span className="font-medium">Código Desbloqueo:</span> {dispositivoSeleccionado.codigo_desbloqueo || 'No configurado'}</div>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <h4 className="font-medium text-gray-700 mb-2">Ubicación</h4>
                  
                  {dispositivoSeleccionado.ubicacion && 
                   dispositivoSeleccionado.ubicacion !== 'null' && 
                   dispositivoSeleccionado.ubicacion !== null &&
                   typeof dispositivoSeleccionado.ubicacion === 'object' &&
                   dispositivoSeleccionado.ubicacion.lat && 
                   dispositivoSeleccionado.ubicacion.lng ? (
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Latitud:</span> 
                        <span className="ml-2 text-gray-700">
                          {dispositivoSeleccionado.ubicacion.lat || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Longitud:</span> 
                        <span className="ml-2 text-gray-700">
                          {dispositivoSeleccionado.ubicacion.lng || 'N/A'}
                        </span>
                      </div>
                      <div><span className="font-medium">Precisión:</span> {dispositivoSeleccionado.ubicacion.accuracy || 'N/A'}m</div>
                      <div><span className="font-medium">Proveedor:</span> {dispositivoSeleccionado.ubicacion.provider || 'N/A'}</div>
                      <div className="col-span-2">
                        <span className="font-medium">Última Actualización:</span> {dispositivoSeleccionado.ubicacion.updated_at ? new Date(dispositivoSeleccionado.ubicacion.updated_at).toLocaleString('es-ES') : 'N/A'}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">Ubicación no disponible</div>
                )}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={cerrarDetalles}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal del Mapa */}
      {mostrarMapa && ubicacionMapa && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Ubicación del Dispositivo
                </h3>
                <button
                  onClick={cerrarMapa}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="font-medium">Latitud:</span> {parseFloat(ubicacionMapa.lat).toFixed(6)}</div>
                  <div><span className="font-medium">Longitud:</span> {parseFloat(ubicacionMapa.lng).toFixed(6)}</div>
                  <div><span className="font-medium">Precisión:</span> {ubicacionMapa.accuracy ? `${ubicacionMapa.accuracy}m` : 'N/A'}</div>
                  <div><span className="font-medium">Proveedor:</span> {ubicacionMapa.provider || 'GPS'}</div>
                </div>
              </div>
              
              {/* Mapa Interactivo con Mapbox */}
              <MapaUbicacion 
                ubicacion={ubicacionMapa}
                dispositivoNombre={dispositivoSeleccionado?.marca + ' ' + dispositivoSeleccionado?.modelo || 'Dispositivo'}
                onCerrar={cerrarMapa}
              />
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={cerrarMapa}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Cerrar Mapa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles del Dispositivo */}
      <DispositivoDetallesModal
        dispositivo={modalDetalles.dispositivo}
        isOpen={modalDetalles.isOpen}
        onClose={cerrarModalDetalles}
      />

      {/* Modal de Código de Desbloqueo Seleccionado */}
      {mostrarModalCodigo && codigoSeleccionado.codigo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Código de Desbloqueo
                </h3>
                <button
                  onClick={cerrarModalCodigo}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Código para dispositivo: {codigoSeleccionado.dispositivoCodigo}
                    </p>
                    <div className="text-4xl font-bold text-blue-700 font-mono tracking-wider mb-4">
                      {codigoSeleccionado.codigo}
                    </div>
                    <p className="text-xs text-gray-500">
                      Este código ha sido marcado como utilizado
                    </p>
                  </div>
                </div>

                {codigoSeleccionado.totalCodigos !== undefined && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-gray-500">Total</p>
                        <p className="text-lg font-semibold text-gray-900">{codigoSeleccionado.totalCodigos}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Disponibles</p>
                        <p className="text-lg font-semibold text-green-600">{codigoSeleccionado.codigosDisponibles}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Usados</p>
                        <p className="text-lg font-semibold text-red-600">{codigoSeleccionado.codigosUsados}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={cerrarModalCodigo}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Códigos Usados */}
      {mostrarModalCodigosUsados && codigosUsadosData.dispositivo && codigosUsadosData.codigos.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Códigos de Desbloqueo Utilizados
                </h3>
                <button
                  onClick={cerrarModalCodigosUsados}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div className="mb-4 p-4 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">
                  Dispositivo: {codigosUsadosData.dispositivo.codigo}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Total de códigos utilizados: {codigosUsadosData.codigos.length} de {codigosUsadosData.dispositivo.codigos_desbloqueo?.length || 0}
                </p>
                <p className="text-xs text-gray-500 mt-1 italic">
                  Ordenados del más reciente al más antiguo
                </p>
              </div>

              <div className="max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {codigosUsadosData.codigos.map((codigo, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 ${
                        index === 0
                          ? 'bg-red-50 border-red-300'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-medium ${
                          index === 0 ? 'text-red-700' : 'text-gray-500'
                        }`}>
                          #{index + 1}
                          {index === 0 && ' (Más reciente)'}
                        </span>
                      </div>
                      <div className={`text-2xl font-bold font-mono tracking-wider ${
                        index === 0 ? 'text-red-700' : 'text-gray-900'
                      }`}>
                        {codigo}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={cerrarModalCodigosUsados}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Información de SIM */}
      {mostrarModalSim && simInfo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto border w-full max-w-2xl shadow-lg rounded-md bg-white flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                Información de Tarjeta SIM
              </h3>
              <button
                onClick={cerrarModalSim}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>
            
            <div className="p-5 bg-green-50 border-b border-gray-200 flex-shrink-0">
              <p className="text-sm font-medium text-gray-700">
                Dispositivo: {simInfo.dispositivoCodigo}
              </p>
            </div>

            <div className="overflow-y-auto flex-1 p-5">
              <div className="space-y-4">
                {/* Información Básica de SIM */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Estado de la Tarjeta SIM</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">Estado:</span>
                      <p className="text-gray-900 mt-1">{simInfo.sim_state || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Operador:</span>
                      <p className="text-gray-900 mt-1">{simInfo.carrier_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Nombre de Visualización:</span>
                      <p className="text-gray-900 mt-1">{simInfo.display_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Información de Identificación */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Identificación</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">ICC ID:</span>
                      <p className="text-gray-900 font-mono mt-1">{simInfo.icc_id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Número de Teléfono:</span>
                      <p className="text-gray-900 font-mono mt-1">{simInfo.subscription_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Número de Línea:</span>
                      <p className="text-gray-900 font-mono mt-1">{simInfo.line1_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Slot de SIM:</span>
                      <p className="text-gray-900 mt-1">
                        {simInfo.sim_slot_index !== undefined && simInfo.sim_slot_index >= 0 
                          ? `Slot ${simInfo.sim_slot_index + 1}` 
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Información de Red */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Información de Red</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-500">MCC:</span>
                      <p className="text-gray-900 mt-1">{simInfo.subscription_mcc || simInfo.mcc || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">MNC:</span>
                      <p className="text-gray-900 mt-1">{simInfo.subscription_mnc || simInfo.mnc || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Código de País:</span>
                      <p className="text-gray-900 mt-1">{simInfo.country_iso || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-500">Código de Operador:</span>
                      <p className="text-gray-900 mt-1">{simInfo.operator_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Información Adicional */}
                {(simInfo.network_type || simInfo.device_id || simInfo.timestamp) && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Información Adicional</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {simInfo.network_type && (
                        <div>
                          <span className="font-medium text-gray-500">Tipo de Red:</span>
                          <p className="text-gray-900 mt-1">{simInfo.network_type}</p>
                        </div>
                      )}
                      {simInfo.device_id && (
                        <div>
                          <span className="font-medium text-gray-500">ID del Dispositivo:</span>
                          <p className="text-gray-900 font-mono mt-1 text-xs">{simInfo.device_id}</p>
                        </div>
                      )}
                      {simInfo.timestamp && (
                        <div>
                          <span className="font-medium text-gray-500">Fecha de Actualización:</span>
                          <p className="text-gray-900 mt-1">
                            {new Date(simInfo.timestamp).toLocaleString('es-ES')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-5 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={cerrarModalSim}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default Dispositivos; 